import React, { useState, useMemo } from 'react';
import { Button, Popover, Checkbox, Space, Divider, Input } from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export interface ColumnOption {
  key: string;
  label: string;
  category?: string;
}

interface ColumnVisibilitySelectorProps {
  columns: ColumnOption[];
  selectedKeys: string[];
  onChange: (selectedKeys: string[]) => void;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
}

const ColumnVisibilitySelector: React.FC<ColumnVisibilitySelectorProps> = ({
  columns,
  selectedKeys,
  onChange,
  disabled = false,
  size = 'large',
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState('');

  // Sort columns alphabetically by label
  const sortedColumns = useMemo(() => {
    return [...columns].sort((a, b) => a.label.localeCompare(b.label));
  }, [columns]);

  // Filter columns based on search text
  const filteredColumns = useMemo(() => {
    if (!searchText.trim()) {
      return sortedColumns;
    }
    const searchLower = searchText.toLowerCase();
    return sortedColumns.filter((col) => col.label.toLowerCase().includes(searchLower));
  }, [sortedColumns, searchText]);

  const handleCheckboxChange = (key: string, checked: boolean) => {
    if (checked) {
      onChange([...selectedKeys, key]);
    } else {
      onChange(selectedKeys.filter((k) => k !== key));
    }
  };

  const handleSelectAll = () => {
    onChange(filteredColumns.map((col) => col.key));
  };

  const handleDeselectAll = () => {
    const filteredKeys = filteredColumns.map((col) => col.key);
    onChange(selectedKeys.filter((key) => !filteredKeys.includes(key)));
  };

  const content = (
    <div style={{ width: 300, maxHeight: 400, overflowY: 'auto' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Input
          placeholder={t('common.search', { defaultValue: 'Search...' })}
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
        />
        <Space>
          <Button type="link" size="small" onClick={handleSelectAll}>
            {t('common.selectAll', { defaultValue: 'Select All' })}
          </Button>
          <Button type="link" size="small" onClick={handleDeselectAll}>
            {t('common.deselectAll', { defaultValue: 'Deselect All' })}
          </Button>
        </Space>
        <Divider style={{ margin: '8px 0' }} />
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {filteredColumns.map((column) => (
            <Checkbox
              key={column.key}
              checked={selectedKeys.includes(column.key)}
              onChange={(e) => handleCheckboxChange(column.key, e.target.checked)}
            >
              {column.label}
            </Checkbox>
          ))}
          {filteredColumns.length === 0 && (
            <div style={{ textAlign: 'center', padding: '16px', color: '#999' }}>
              {t('common.noData', { defaultValue: 'No data found' })}
            </div>
          )}
        </Space>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      title={t('common.columnVisibility', { defaultValue: 'Column Visibility' })}
      trigger="click"
      open={visible}
      onOpenChange={setVisible}
      placement="bottomRight"
    >
      <Button icon={<EyeOutlined />} disabled={disabled} size={size}>
        {t('common.dataPreview', { defaultValue: 'Data Preview' })}
      </Button>
    </Popover>
  );
};

export default ColumnVisibilitySelector;
